import { useState } from 'react';
import Drawer, { DrawerProps } from '@liquidweb-dev/undercurrent/Drawer';
import DrawerHeader from '@liquidweb-dev/undercurrent/DrawerHeader';
import DrawerContent from '@liquidweb-dev/undercurrent/DrawerContent';
import DrawerFooter from '@liquidweb-dev/undercurrent/DrawerFooter';
import Button from '@liquidweb-dev/undercurrent/Button';
import Stack from '@liquidweb-dev/undercurrent/Stack';
import Typography from '@liquidweb-dev/undercurrent/Typography';
import FormControl from '@liquidweb-dev/undercurrent/FormControl';
import RadioGroup from '@liquidweb-dev/undercurrent/RadioGroup';
import ThreatDownIcon from '@liquidweb-dev/undercurrent/icons/platform/ThreatDown';
import Imunify360Icon from '@liquidweb-dev/undercurrent/icons/platform/Imunify360';
import { AddOnContent, DrawerAddOnHeader } from 'components/DrawerAddOn';
import useAddOns from 'pages/servers/Settings/SettingsAddOns/useAddOns';
import Box from '@liquidweb-dev/undercurrent/Box';
import SelectionCard from './SelectionCard';

export interface DrawerAddServerProtectionProps extends DrawerProps {
	/** Callback function when drawer is closed */
	onClose: () => void;
	/** Unique identifier for the server */
	uniqId: string;
	/** Server hostname to display */
	hostname: string;
	/** Callback function when security is successfully added */
	onSuccess?: () => void;
}

type AddOnCode = 'PKG-ThreatDown' | 'PKG-Imunify360';
type AddOnSelection = AddOnCode | null;

/**
 * Drawer component for adding server protection add-ons (ThreatDown or Imunify360)
 * to a server. Handles the selection flow and navigates to the checkout process.
 */
const DrawerAddServerProtection = ({
	onClose,
	uniqId,
	hostname,
	onSuccess,
	...rest
}: DrawerAddServerProtectionProps) => {
	const [selectedAddOn, setSelectedAddOn] = useState<AddOnSelection>(null);
	const [showCheckout, setShowCheckout] = useState(false);
	const { open } = rest;

	const {
		canAddThreatDown,
		canAddImunify360,
		regionId = 1,
	} = useAddOns(uniqId, open);

	const handleClose = () => {
		setSelectedAddOn(null);
		setShowCheckout(false);
		onClose();
	};

	const handleContinue = () => {
		if (selectedAddOn) {
			setShowCheckout(true);
		}
	};

	const handleCheckoutSuccess = () => {
		onSuccess?.();
		handleClose();
	};

	const handleSelectionChange = (
		event: React.ChangeEvent<HTMLInputElement>,
	) => {
		setSelectedAddOn(event.target.value as AddOnSelection);
	};

	return (
		<Drawer anchor="right" onClose={handleClose} {...rest}>
			{showCheckout && selectedAddOn ? (
				<>
					<DrawerAddOnHeader onClose={handleClose} />
					<AddOnContent
						code={selectedAddOn}
						uniqId={uniqId}
						regionId={regionId}
						onSuccess={handleCheckoutSuccess}
						enableQueries={Boolean(open)}
					/>
				</>
			) : (
				<>
					<DrawerHeader
						onClose={handleClose}
						description="Choose a security solution to protect your server"
					>
						Security settings
					</DrawerHeader>
					<DrawerContent>
						<Stack
							sx={{
								gap: 200,
							}}
						>
							<Typography variant="body-sm">
								Select security type for{' '}
								<Box
									component="span"
									sx={{
										color: ({ vars }) => vars.palette.uc.highlight.text.main,
									}}
								>
									{hostname}
								</Box>
							</Typography>
							<FormControl>
								<RadioGroup
									aria-labelledby="security-type-group"
									aria-label="Select security add-on type"
									name="security-type"
									value={selectedAddOn || ''}
									onChange={handleSelectionChange}
								>
									<Stack
										sx={{
											gap: 150,
										}}
									>
										<SelectionCard
											value="PKG-ThreatDown"
											Icon={ThreatDownIcon}
											title="ThreatDown"
											description="ThreatDown adds advanced, real-time monitoring to watch and stop attacks before they happen. It detects threats like Endpoint Detection and Response (EDR) with expert human oversight (MDR), giving your server a proactive defense layer against today's stealthiest threats."
											checked={selectedAddOn === 'PKG-ThreatDown'}
											disabled={!canAddThreatDown}
										/>
										<SelectionCard
											value="PKG-Imunify360"
											Icon={Imunify360Icon}
											title="Imunify360"
											description="Imunify360 helps protect your web server by detecting and cleaning malware, blocking known threats with a firewall and web application firewall, and automatically patching server-level vulnerabilities."
											checked={selectedAddOn === 'PKG-Imunify360'}
											disabled={!canAddImunify360}
										/>
									</Stack>
								</RadioGroup>
							</FormControl>
						</Stack>
					</DrawerContent>
					<DrawerFooter sx={{ gap: 100 }}>
						<Button
							fullWidth
							variant="primary"
							onClick={handleContinue}
							disabled={!selectedAddOn}
							size="large"
						>
							Continue
						</Button>
						<Button
							fullWidth
							variant="ghost"
							onClick={handleClose}
							size="large"
						>
							Cancel
						</Button>
					</DrawerFooter>
				</>
			)}
		</Drawer>
	);
};

export default DrawerAddServerProtection;
